import z from "zod";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import type { UserBase } from "@common/types/user";
import {
    ColumnSchema,
    View,
    ViewDensity,
    ViewSchema,
    ViewType,
} from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import { TaskPreviewAssigneeCell } from "./cells/TaskPreviewAssigneeCell";
import { TaskPreviewDefaultCell } from "./cells/TaskPreviewDefaultCell";
import { TaskPreviewNameCell } from "./cells/TaskPreviewNameCell";

enum TaskPreviewColumn {
    NAME = "name",
    ASSIGNEE = "assignee",
    TASK_TYPE = "taskType",
    CLOSE_STAGE = "closeStage",
    DUE_DATE = "dueDate",
    BLOCKED_BY = "blockedBy",
    RELATED_TO = "relatedTo",
}

export interface TaskPreviewRow {
    id: string;
    name: string;
    assignee: string;
    assigneeUser: Pick<
        UserBase,
        "name" | "avatar" | "iconColor" | "email"
    > | null;
    taskType: string;
    closeStage: string;
    dueDate: string;
    blockedBy: string;
    relatedTo: string;
    skipReason?: string | null;
    isGroupRow?: boolean;
    children?: TaskPreviewRow[];
}

export const CLIENT_HIDDEN_ROLES: ReadonlySet<TaskTemplateAssigneeRole> =
    new Set([
        TaskTemplateAssigneeRole.PREPARER,
        TaskTemplateAssigneeRole.REVIEWER,
    ]);

export const createDefaultTaskPreviewView = (): View => ({
    type: ViewType.TASK_GROUP,
    columns: [
        { id: TaskPreviewColumn.NAME },
        { id: TaskPreviewColumn.ASSIGNEE },
        { id: TaskPreviewColumn.TASK_TYPE },
        { id: TaskPreviewColumn.CLOSE_STAGE },
        { id: TaskPreviewColumn.DUE_DATE },
        { id: TaskPreviewColumn.BLOCKED_BY },
        { id: TaskPreviewColumn.RELATED_TO },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.MD,
    },
});

export const taskPreviewViewConfig: ViewConfig<
    TaskPreviewRow,
    TaskPreviewColumn,
    never
> = {
    columns: {
        [TaskPreviewColumn.NAME]: {
            accessorKey: "name",
            name: "Name",
            header: "Name",
            immutable: true,
            minSize: 240,
            maxSize: 600,
            cell: TaskPreviewNameCell,
        },
        [TaskPreviewColumn.ASSIGNEE]: {
            accessorKey: "assignee",
            name: "Assignee",
            header: "Assignee",
            minSize: 160,
            maxSize: 220,
            cell: TaskPreviewAssigneeCell,
        },
        [TaskPreviewColumn.TASK_TYPE]: {
            accessorKey: "taskType",
            name: "Type",
            header: "Type",
            minSize: 140,
            maxSize: 220,
            cell: TaskPreviewDefaultCell,
        },
        [TaskPreviewColumn.CLOSE_STAGE]: {
            accessorKey: "closeStage",
            name: "Stage",
            header: "Stage",
            minSize: 150,
            maxSize: 220,
            cell: TaskPreviewDefaultCell,
        },
        [TaskPreviewColumn.DUE_DATE]: {
            accessorKey: "dueDate",
            name: "Due date",
            header: "Due date",
            minSize: 130,
            maxSize: 170,
            cell: TaskPreviewDefaultCell,
        },
        [TaskPreviewColumn.BLOCKED_BY]: {
            accessorKey: "blockedBy",
            name: "Blocked by",
            header: "Blocked by",
            minSize: 160,
            maxSize: 320,
            cell: TaskPreviewDefaultCell,
        },
        [TaskPreviewColumn.RELATED_TO]: {
            accessorKey: "relatedTo",
            name: "Related to",
            header: "Related to",
            minSize: 160,
            maxSize: 320,
            cell: TaskPreviewDefaultCell,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: TaskPreviewColumn.NAME,
        childrenKey: "children",
    },
};

const TaskPreviewColumnSchema = ColumnSchema.extend({
    id: z.nativeEnum(TaskPreviewColumn),
});

export const TaskPreviewViewSchema = ViewSchema.extend({
    type: z.literal(ViewType.TASK_GROUP),
    columns: z.array(TaskPreviewColumnSchema),
});
